import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import MusicNoteIcon from "@mui/icons-material/MusicNote"; // TikTok ka official icon MUI mein nahi hai
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../../../components/AppLoader";
import GlobalButton from "../../../../components/CustomButton";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import UploadImageAvatar from "../../../../components/UploadImage";
import RomanianPhoneInput from "../../../../components/romaniaPhoneInput";
import { baseUrl } from "../../../../services";
// import { registerInfluencer } from "../../../../services/influencer/influencerApiFn";
import { creatorSendCode } from "../../../../services/creator/creatorApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { registerCreatorValidation } from "../../../../utils/Validation";

// Dummy types — baad mein API se aayenge
const INFLUENCER_TYPES = [
  { value: "food", label: "Food & Cooking" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "fitness", label: "Fitness & Health" },
  { value: "family", label: "Family & Parenting" },
  { value: "other", label: "Other" },
];

const initialValues = {
  image: null,
  influencerType: "",
  name: "",
  email: "",
  phoneNumber: "",
  password: "",
  facebook: "",
  tiktok: "",
  instagram: "",
};

const initialErrors = {
  influencerType: false,
  name: false,
  email: false,
  isEmailValid: false,
  phoneNumber: false,
  password: false,
  isPasswordStrong: false,
  socialRequired: false,
  facebook: false,
  tiktok: false,
  instagram: false,
};

const InfluencerRegistrationForm = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [inputValues, setInputValues] = useState(initialValues);
  const [inputError, setInputError] = useState(initialErrors);
  const [showPass, setShowPass] = useState(false);

  const handleChangeValues = (event) => {
    const { name, value } = event.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setInputError((prev) => ({
      ...prev,
      [name]: false,
      isEmailValid: name === "email" ? false : prev.isEmailValid,
      isPasswordStrong: name === "password" ? false : prev.isPasswordStrong,
      socialRequired: ["facebook", "tiktok", "instagram"].includes(name)
        ? false
        : prev.socialRequired,
    }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", file);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prev) => ({ ...prev, image: response?.data?.url }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    const hasError = registerCreatorValidation(inputValues, setInputError);
    if (hasError) return;

    try {
      setIsLoading(true);

      const payload = {
        email: inputValues.email,
        purpose: "Registration",
        role: "Creator",
      };

      const response = await creatorSendCode(payload);

      if (response?.data?.status === "success") {
        navigate(`/register/creator/verification`, {
          state: {
            inputValues: { ...inputValues },
          },
        });
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar(
        "error",
        error?.response?.data?.message || error?.message,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const socialFields = [
    {
      name: "facebook",
      label: "Facebook",
      placeholder: "https://facebook.com/yourpage",
      icon: <FacebookIcon sx={{ color: "#1877F2" }} />,
    },
    {
      name: "tiktok",
      label: "TikTok",
      placeholder: "https://tiktok.com/@yourname",
      icon: <MusicNoteIcon />,
    },
    {
      name: "instagram",
      label: "Instagram",
      placeholder: "https://instagram.com/yourname",
      icon: <InstagramIcon sx={{ color: "#E4405F" }} />,
    },
  ];

  return (
    <Box component="form" sx={{ mt: 3 }} onSubmit={(e) => e.preventDefault()}>
      <AppLoader isVisible={isLoading} />
      <Grid container spacing={2}>
        {/* 1. Photo (Optional) */}
        <Grid item xs={12}>
          <Stack alignItems="center" spacing={0.5}>
            <UploadImageAvatar
              error={false}
              image={inputValues.image}
              handleImageChange={handleImageChange}
            />
            <Typography variant="caption" color="text.secondary">
              {t("photoOptional", "Photo (optional)")}
            </Typography>
          </Stack>
        </Grid>

        {/* 2. Influencer Type */}
        <Grid item xs={12}>
          <Label
            labelText={t("influencerType", "Influencer Type")}
            htmlFor="influencerType"
            required
          />
          <FormControl fullWidth error={inputError.influencerType}>
            <Select
              id="influencerType"
              name="influencerType"
              value={inputValues.influencerType}
              onChange={handleChangeValues}
              displayEmpty
              size="small"
            >
              <MenuItem value="" disabled>
                {t("selectInfluencerType", "Select influencer type")}
              </MenuItem>
              {INFLUENCER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {inputError.influencerType && (
              <FormHelperText sx={{ color: "custom.errorRed" }}>
                {t("influencerTypeRequired", "Influencer type is required")}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* 3. Name */}
        <Grid item xs={12}>
          <Label labelText={t("name")} htmlFor="name" required />
          <TextInput
            id="name"
            required
            inputError={inputError.name}
            helperText={inputError.name && t("nameRequired")}
            placeholder={`${t("enter")} ${t("name")}`}
            type="text"
            name="name"
            value={inputValues.name}
            onChange={handleChangeValues}
          />
        </Grid>

        {/* 4. Email */}
        <Grid item xs={12}>
          <Label labelText={t("emailAddress")} htmlFor="email" required />
          <TextInput
            id="email"
            required
            inputError={inputError.email || inputError.isEmailValid}
            helperText={
              inputError.email
                ? t("emailRequired")
                : inputError.isEmailValid
                  ? t("invalidEmailFormat")
                  : null
            }
            placeholder={`${t("enter")} ${t("emailAddress")}`}
            type="email"
            name="email"
            value={inputValues.email}
            onChange={handleChangeValues}
          />
        </Grid>

        {/* 5. Phone Number */}
        <Grid item xs={12}>
          <Label labelText={t("phoneNumber")} htmlFor="phoneNumber" required />
          <RomanianPhoneInput
            inputError={inputError.phoneNumber}
            value={inputValues.phoneNumber}
            onChange={(value) => {
              setInputValues((prev) => ({ ...prev, phoneNumber: value }));
              setInputError((prev) => ({ ...prev, phoneNumber: false }));
            }}
          />
          {inputError.phoneNumber && (
            <FormHelperText sx={{ color: "custom.errorRed" }}>
              {t("phoneRequired", "Valid phone number is required")}
            </FormHelperText>
          )}
        </Grid>

        {/* 6. Password */}
        <Grid item xs={12}>
          <Label labelText={t("password")} htmlFor="password" required />
          <TextInput
            id="password"
            required
            inputError={inputError.password || inputError.isPasswordStrong}
            helperText={
              inputError.password
                ? t("passwordRequired")
                : inputError.isPasswordStrong
                  ? t("passwordRequirements")
                  : null
            }
            placeholder={`${t("enter")} ${t("password")}`}
            type={showPass ? "text" : "password"}
            name="password"
            autoComplete="new-password"
            value={inputValues.password}
            onChange={handleChangeValues}
            InputEndIcon={
              showPass ? (
                <RemoveRedEyeIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowPass(false)}
                />
              ) : (
                <VisibilityOffIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowPass(true)}
                />
              )
            }
          />
        </Grid>

        {/* 7. Social Media Links */}
        <Grid item xs={12}>
          <Typography fontWeight={600} mt={1}>
            {t("socialMediaLinks", "Social Media Links")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("atLeastOneSocial", "Add at least one profile link")}
          </Typography>
          {inputError.socialRequired && (
            <FormHelperText sx={{ color: "custom.errorRed" }}>
              {t("socialRequired", "Please add at least one social media link")}
            </FormHelperText>
          )}
        </Grid>

        {socialFields.map((field) => (
          <Grid item xs={12} key={field.name}>
            <Label labelText={field.label} htmlFor={field.name} />
            <TextInput
              id={field.name}
              inputError={inputError[field.name] || inputError.socialRequired}
              helperText={
                inputError[field.name] &&
                t("invalidSocialLink", `Enter a valid ${field.label} link`)
              }
              placeholder={field.placeholder}
              type="url"
              name={field.name}
              value={inputValues[field.name]}
              onChange={handleChangeValues}
              InputEndIcon={field.icon}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={4}>
        <GlobalButton
          variant="contained"
          btnText="submitApplication"
          handleClickBtn={handleRegister}
        />
      </Box>
    </Box>
  );
};

export default InfluencerRegistrationForm;
